<template>
    <!-- K-1 (UI-Gate Welle 2, welle2-ui-report.md): bei aktivem
         "Projekt-Standard verwenden" waren die Checkboxen zwar technisch
         `disabled`, sahen aber unverändert bedienbar aus — kein Hinweis,
         warum ein Klick wirkungslos bleibt. `opacity-50`/`cursor-not-allowed`
         auf dem ganzen Matrix-Block machen den gesperrten Zustand sichtbar. -->
    <table
        class="min-w-full divide-y divide-gray-200"
        :class="disabled ? 'opacity-50 cursor-not-allowed' : ''"
    >
        <thead>
            <tr>
                <th
                    scope="col"
                    class="py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                ></th>
                <th
                    scope="col"
                    class="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                    {{ $t("columns.internal") }}
                </th>
                <th
                    scope="col"
                    class="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                    {{ $t("columns.buyer") }}
                </th>
            </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
            <tr v-for="column in columns" :key="column.key">
                <td class="py-2 text-sm text-gray-900">
                    {{ $t(column.labelKey) }}
                    <!-- Die Begründung nennt den Berechnungsmodus nicht,
                         sondern nur, DASS die Spalte nicht ausblendbar ist.
                         Ursprünglich war das eine Geheimhaltungsauflage
                         (Entscheid 15 vom 2026-08-16: der Modus war
                         superadmin-only, Entscheid 5) — die ist mit dem
                         Auftrag vom 2026-08-20 ENTFALLEN, seither steht der
                         Modus jedem Agenturmitglied auf der Projektseite.
                         Der kurze Text bleibt trotzdem: er beantwortet die
                         Frage, die an dieser Stelle gestellt wird («warum
                         kann ich das nicht abwählen»), und der Modus steht
                         eine Seite weiter ausgeschrieben. -->
                    <span
                        v-if="isLocked(column.key)"
                        class="block text-xs italic text-gray-500"
                        data-test="locked-reason"
                    >
                        {{ $t("columns.notHideable") }}
                    </span>
                </td>
                <td class="py-2 text-center">
                    <input
                        type="checkbox"
                        class="rounded"
                        :disabled="disabled || isLocked(column.key)"
                        :checked="isChecked('internal', column.key)"
                        @change="toggle('internal', column.key, $event.target.checked)"
                    />
                </td>
                <td class="py-2 text-center">
                    <input
                        type="checkbox"
                        class="rounded"
                        :disabled="disabled || isLocked(column.key)"
                        :checked="isChecked('buyer', column.key)"
                        @change="toggle('buyer', column.key, $event.target.checked)"
                    />
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
// Thema 1 (Spec docs/superpowers/specs/2026-08-04-welle2-spalten-sichtbarkeit-design.md):
// gemeinsame Checkbox-Matrix (intern/käufer × die vier `Project::COLUMN_KEYS`)
// für die Projekt- UND die Wohnungs-Modal-Page. Reine Props-in/Events-raus-
// Komponente ohne eigenen Fetch — die Werte kommen vom `useForm`-State der
// jeweiligen Modal-Page (v-model:internal / v-model:buyer). Die Kopfspalten-
// Labels laufen bewusst über die bereits bestehenden Tabellen-Keys
// (apartments.budgetCHF/offeredCHF/baseFee/consultancyFee) statt über neue
// Kurz-Keys — Norm: bestehende Keys wiederverwenden.
const COLUMNS = [
    { key: "budget", labelKey: "apartments.budgetCHF" },
    { key: "mehrkosten", labelKey: "apartments.offeredCHF" },
    { key: "gebuehr", labelKey: "apartments.baseFee" },
    { key: "honorar", labelKey: "apartments.consultancyFee" },
];

export default {
    props: {
        internal: { type: Array, required: true },
        buyer: { type: Array, required: true },
        disabled: { type: Boolean, default: false },
        // Auftrag 2026-08-16, Entscheide 14 bis 16: Spalten, die diese Agentur
        // nicht ausblenden darf. Sie bleiben SICHTBAR (nicht entfernt), sind
        // aber nicht bedienbar. Angezeigt werden sie als angehakt, weil sie
        // effektiv sichtbar sind — der Formularzustand bleibt davon
        // unberührt, damit ein Speichern die gespeicherte Konfiguration nicht
        // überschreibt (Entscheid 16).
        lockedColumns: { type: Array, default: () => [] },
        // Auftrag Luca, 2026-08-25, Entscheid 1.1: die Kostenspalte heisst im
        // budgetbasierten Modus «Offeriert» — auch hier, sonst benennt die
        // Einstellungsmaske eine andere Spalte als die Tabelle, die sie
        // konfiguriert. Fehlt die Prop, bleibt es beim Offerten-Wortlaut.
        //
        // Der Modus ist hier kein Geheimnis mehr: seit dem 2026-08-20 steht er
        // fuer jedes Agenturmitglied auf der Projektseite. Die fruehere
        // Auflage, ihn an dieser Matrix nicht zu verraten, ist mit jenem
        // Auftrag entfallen.
        isBudgetBased: { type: Boolean, default: false },
    },
    emits: ["update:internal", "update:buyer"],
    computed: {
        columns() {
            return COLUMNS.map((column) =>
                column.key === "mehrkosten" && this.isBudgetBased
                    ? { ...column, labelKey: "apartments.quotedCHF" }
                    : column
            );
        },
    },
    methods: {
        isLocked(key) {
            return this.lockedColumns.includes(key);
        },
        isChecked(audience, key) {
            if (this.isLocked(key)) {
                return true;
            }
            const current = audience === "internal" ? this.internal : this.buyer;
            return current.includes(key);
        },
        toggle(audience, key, checked) {
            // Gürtel zum `disabled` am Element: ein gesperrter Schlüssel darf
            // den Formularzustand unter keinen Umständen verändern.
            if (this.isLocked(key)) {
                return;
            }
            const current = audience === "internal" ? this.internal : this.buyer;
            const next = checked
                ? [...current, key]
                : current.filter((existing) => existing !== key);
            this.$emit(`update:${audience}`, next);
        },
    },
};
</script>

<style scoped></style>
